import { http } from "./client";
import type {
  AttendanceReportRow,
  FieldWorkReportRow,
  FieldWorkSummary,
  LateReportRow,
  LeaveReportRow,
  MonthlySummary,
  OvertimeReportRow,
  PayrollRegister,
  PayrollReportRow,
} from "@/types/domain";

/** The download formats every report endpoint except bank-transfer accepts. */
export type ExportFormat = "csv" | "xlsx" | "pdf";

/** File extension per format - they happen to match, but the mapping is explicit. */
export const EXPORT_EXTENSION: Record<ExportFormat, string> = { csv: "csv", xlsx: "xlsx", pdf: "pdf" };

/** One line of the Geo-Fence Verification Report - never carries raw lat/lng
 * (see ReportService.GeoFenceVerificationRow's own privacy-safe-reporting
 * comment); geo-fence name, distance and accuracy are what's shown instead. */
export interface GeoFenceVerificationRow {
  employee_code: string;
  employee_name: string;
  date: string;
  direction: string;
  branch_name: string;
  geo_fence_name: string;
  distance_meters?: number;
  accuracy_meters?: number;
  result: "verified" | "outside_fence" | "low_accuracy" | "not_configured";
}

/** The "Geo-Fencing at a glance" counts card - the admin list page's stat
 * strip and the tenant dashboard card both read this same snapshot. */
export interface GeoFenceDashboardSnapshot {
  total_geo_fences: number;
  active_geo_fences: number;
  branches_covered: number;
  verified_today: number;
  outside_fence_today: number;
}

// ---------------------------------------------------------------------------
// Payroll Reporting Phase 3 - department/earnings/deductions/net-pay/
// statutory/LOP/variance/salary-history/F&F/payment-summary reports, all
// under /reports/payroll/... Unlike PayrollReportRow above (a decimal
// string, per the backend's no-binary-floats-for-currency convention), every
// money field on these newer reports is a genuine JSON number - this report
// generation serializes amounts differently, confirmed against the live
// contract. formatMoney/formatNumber both accept either shape so this
// doesn't complicate rendering.
// ---------------------------------------------------------------------------

export interface PayrollSummaryReport {
  period: string;
  status: string;
  employee_count: number;
  working_days: number;
  paid_days: number;
  totals: PayrollReportRow;
  /** Omitted (not zeroed) when no run exists for the prior period. */
  previous?: PayrollReportRow;
}

export interface DepartmentPayrollRow {
  department: string;
  employee_count: number;
  gross_earnings: number;
  total_deductions: number;
  employer_pf: number;
  employer_esi: number;
  overtime: number;
  loss_of_pay: number;
  net_payroll: number;
}

export interface PayrollComponentRow {
  employee_code: string;
  employee_name: string;
  department: string;
  component: string;
  amount: number;
}

/** Shared response shape for both /reports/payroll/earnings and
 * /reports/payroll/deductions - the deductions endpoint just carries
 * deduction components in the same rows instead of earnings ones. */
export interface PayrollComponentReport {
  period: string;
  rows: PayrollComponentRow[];
  /** Keyed by component display name, e.g. "Basic", "PF Employee". */
  component_totals: Record<string, number>;
  /** Keyed by employee_code. */
  employee_totals: Record<string, number>;
  grand_total: number;
}

export interface NetPayRow {
  employee_code: string;
  employee_name: string;
  department: string;
  gross_earnings: number;
  total_deductions: number;
  net_pay: number;
  /** "" / undefined when no payment batch exists yet for this run. */
  payment_status?: string;
}

export type StatutoryReportType = "pf" | "esi" | "pt" | "tds";

export interface StatutoryReportRow {
  employee_code: string;
  employee_name: string;
  wages: number;
  employee_contribution: number;
  /** PT and TDS reports carry no employer contribution - 0/omitted for those two kinds. */
  employer_contribution?: number;
}

export interface StatutoryReport {
  kind: StatutoryReportType;
  period: string;
  rows: StatutoryReportRow[];
  total_employee_contribution: number;
  total_employer_contribution?: number;
}

export interface LOPRow {
  employee_code: string;
  employee_name: string;
  department: string;
  lop_days: number;
  lop_amount: number;
  /** Always "attendance" in this system. */
  source: string;
}

export interface VarianceMetric {
  previous: number;
  current: number;
  difference: number;
  /** null when `previous` is 0 - a brand-new figure with nothing to compare
   * against. Render as "N/A"/"—", never as "0%". A negative difference/
   * percent is a real, valid value (payroll can shrink month over month),
   * not an error state. */
  percent: number | null;
}

export interface PayrollVarianceReport {
  current_period: string;
  previous_period: string;
  employee_count: VarianceMetric;
  gross_payroll: VarianceMetric;
  total_deductions: VarianceMetric;
  employer_contributions: VarianceMetric;
  overtime: VarianceMetric;
  loss_of_pay: VarianceMetric;
  net_payroll: VarianceMetric;
}

export interface EmployeeSalaryHistoryEntry {
  period: string;
  basic: number;
  gross_earnings: number;
  total_deductions: number;
  net_pay: number;
}

/** One employee's multi-period salary history - never carries bank details. */
export interface EmployeeSalaryHistory {
  employee_code: string;
  employee_name: string;
  department: string;
  designation: string;
  history: EmployeeSalaryHistoryEntry[];
}

export interface FinalSettlementReportRow {
  employee_code: string;
  employee_name: string;
  exit_date: string;
  pending_salary: number;
  leave_encashment: number;
  notice_recovery: number;
  /** Always 0 - this system has no gratuity calculation. Still rendered. */
  gratuity: number;
  other_earnings: number;
  other_deductions: number;
  total_earnings: number;
  total_deductions: number;
  final_payable: number;
  settlement_status: string;
  payment_status: string;
}

export interface FinalSettlementReport {
  rows: FinalSettlementReportRow[];
  totals: FinalSettlementReportRow;
  count: number;
}

export interface PaymentSummaryRow {
  batch_reference: string;
  period_year: number;
  period_month: number;
  status: string;
  employee_count: number;
  total_amount: number;
  /** Payment item status -> count, e.g. {"successful": 11, "failed": 1}. */
  breakdown: Record<string, number>;
}

export interface PaymentSummaryReport {
  year: number;
  rows: PaymentSummaryRow[];
  count: number;
}

export const reportsApi = {
  attendance: (period: string, department_id?: string) =>
    http.get<{ period: string; rows: AttendanceReportRow[]; count: number }>("/reports/attendance", {
      period,
      department_id,
    }),
  payroll: (period: string) => http.get<PayrollRegister>("/reports/payroll", { period }),
  // "Team leave report" - scoped identically to attendance above (a Manager
  // sees only their own team; department_id further narrows within that).
  leave: (period: string, department_id?: string) =>
    http.get<{ period: string; rows: LeaveReportRow[]; count: number }>("/reports/leave", { period, department_id }),
  late: (period: string) => http.get<{ period: string; rows: LateReportRow[]; count: number }>("/reports/late", { period }),
  overtime: (period: string) =>
    http.get<{ period: string; rows: OvertimeReportRow[]; count: number }>("/reports/overtime", { period }),
  summary: (period: string) => http.get<MonthlySummary>("/reports/summary", { period }),
  dashboard: () => http.get<{ current: MonthlySummary; previous: MonthlySummary | null }>("/dashboard"),
  geoFenceVerification: (period: string, branch_id?: string) =>
    http.get<{ period: string; rows: GeoFenceVerificationRow[]; count: number }>("/reports/geo-fence-verification", {
      period,
      branch_id,
    }),
  geoFenceSummary: () => http.get<GeoFenceDashboardSnapshot>("/reports/geo-fence-summary"),

  // Field Work Reporting (Phase 4) - employee-wise field work report and
  // aggregate summary. Scoped exactly like the attendance report.
  fieldWorkReport: (period: string, department_id?: string) =>
    http.get<{ period: string; rows: FieldWorkReportRow[]; count: number }>("/reports/field-work", {
      period,
      department_id,
    }),
  fieldWorkSummary: (period: string) =>
    http.get<FieldWorkSummary>("/reports/field-work/summary", { period }),

  // ---- Payroll Reporting Phase 3 (11 new report endpoints) ----
  payrollSummary: (period: string) => http.get<PayrollSummaryReport>("/reports/payroll/summary", { period }),
  payrollDepartment: (period: string) =>
    http.get<{ period: string; rows: DepartmentPayrollRow[]; count: number }>("/reports/payroll/department", {
      period,
    }),
  payrollEarnings: (period: string) => http.get<PayrollComponentReport>("/reports/payroll/earnings", { period }),
  payrollDeductions: (period: string) => http.get<PayrollComponentReport>("/reports/payroll/deductions", { period }),
  payrollNetPay: (period: string) =>
    http.get<{ period: string; rows: NetPayRow[]; count: number }>("/reports/payroll/net-pay", { period }),
  payrollStatutory: (period: string, type: StatutoryReportType) =>
    http.get<StatutoryReport>("/reports/payroll/statutory", { period, type }),
  payrollLOP: (period: string) =>
    http.get<{ period: string; rows: LOPRow[]; count: number }>("/reports/payroll/lop", { period }),
  /** compare_period is optional - the backend defaults to the immediately
   * preceding period when it's omitted. */
  payrollVariance: (period: string, comparePeriod?: string) =>
    http.get<PayrollVarianceReport>("/reports/payroll/variance", { period, compare_period: comparePeriod }),
  /** One employee's multi-period salary history - the per-employee drill-
   * down surfaced from PayslipDetailPage, not a top-level Reports tab. */
  payrollSalaryHistory: (employeeId: string, limit = 12) =>
    http.get<EmployeeSalaryHistory>("/reports/payroll/salary", { employee_id: employeeId, limit }),
  /** Requires final_settlement.read in addition to report.read - callers
   * must gate this on can("final_settlement.read"), not just report.read. */
  payrollFnf: (from: string, to: string, status?: string) =>
    http.get<FinalSettlementReport>("/reports/payroll/fnf", { from, to, status }),
  /** Requires payroll.payment.view in addition to report.read - callers
   * must gate this on can("payroll.payment.view"), not just report.read. */
  payrollPayments: (year: number, month?: number) =>
    http.get<PaymentSummaryReport>("/reports/payroll/payments", { year, month }),

  downloadAttendance: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/attendance", { period, format }),
  downloadPayroll: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/payroll", { period, format }),
  downloadLeave: (period: string, format: ExportFormat = "csv") => http.download("/reports/leave", { period, format }),
  downloadLate: (period: string, format: ExportFormat = "csv") => http.download("/reports/late", { period, format }),
  downloadOvertime: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/overtime", { period, format }),
  downloadEmployees: (format: ExportFormat = "csv") => http.download("/reports/employees", { format }),
  downloadGeoFenceVerification: (period: string, format: ExportFormat = "csv", branch_id?: string) =>
    http.download("/reports/geo-fence-verification", { period, format, branch_id }),
  downloadFieldWork: (period: string, format: ExportFormat = "csv", department_id?: string) =>
    http.download("/reports/field-work", { period, format, department_id }),

  // ---- Payroll Reporting Phase 3 downloads (every report above that takes
  // ?format=csv|xlsx|pdf; summary/variance/salary/payments have no export). ----
  downloadPayrollDepartment: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/payroll/department", { period, format }),
  downloadPayrollEarnings: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/payroll/earnings", { period, format }),
  downloadPayrollDeductions: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/payroll/deductions", { period, format }),
  downloadPayrollNetPay: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/payroll/net-pay", { period, format }),
  downloadPayrollStatutory: (period: string, type: StatutoryReportType, format: ExportFormat = "csv") =>
    http.download("/reports/payroll/statutory", { period, type, format }),
  downloadPayrollLOP: (period: string, format: ExportFormat = "csv") =>
    http.download("/reports/payroll/lop", { period, format }),
  downloadPayrollFnf: (from: string, to: string, status: string | undefined, format: ExportFormat = "csv") =>
    http.download("/reports/payroll/fnf", { from, to, status, format }),

  /**
   * The bank transfer file takes no format: it is a fixed column layout a bank
   * parses, not a report a person reads, so the backend keeps it CSV-only and
   * ignores ?format entirely. See handler.BankTransferReport.
   */
  downloadBankTransferCSV: (period: string) => http.download("/reports/bank-transfer", { period }),
};
